"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { verktygslasKlient } from "@/lib/verktygslas-klient";

/**
 * Global tool-lock state. Calculator and the GeoGebra graph tool are locked
 * independently so a class can allow one without the other. UppgiftPage sets
 * the locks (and the multiplication-table flag) from the focused uppgift's
 * digitalaVerktyg flag combined with the per-class settings. CalculatorOverlay
 * reads calcLocked; GraphButton/GraphOverlay read graphLocked.
 *
 * A task page that unmounts does not unlock here directly: it schedules the
 * release in verktygslas-klient.ts, which owns both halves (this state and the
 * server post) so the desktop and the phone agree. Every local set cancels a
 * pending release, so the release can never undo the next page's lock.
 */
interface ToolLockContextValue {
  calcLocked: boolean;
  graphLocked: boolean;
  /** Whether the multiplication-table tab is shown in the calculator. */
  multiplicationTableEnabled: boolean;
  setCalcLocked: (b: boolean) => void;
  setGraphLocked: (b: boolean) => void;
  setMultiplicationTableEnabled: (b: boolean) => void;
}

const Ctx = createContext<ToolLockContextValue | null>(null);

export function ToolLockProvider({ children }: { children: ReactNode }) {
  const [calcLocked, setCalcLockedState] = useState(false);
  const [graphLocked, setGraphLockedState] = useState(false);
  // Default true: preserves the previous always-on multiplication table on
  // pages that never set it (e.g. outside a course context).
  const [multiplicationTableEnabled, setMultiplicationTableEnabledState] =
    useState(true);

  const setCalcLocked = useCallback((b: boolean) => {
    verktygslasKlient.avbrytSlapp();
    setCalcLockedState(b);
  }, []);
  const setGraphLocked = useCallback((b: boolean) => {
    verktygslasKlient.avbrytSlapp();
    setGraphLockedState(b);
  }, []);

  // The deferred release unlocks through this state; on pagehide a pending
  // release is sent at once. Unregistering also clears a pending timer.
  useEffect(() => {
    verktygslasKlient.sattLokalSlapp(() => {
      setCalcLockedState(false);
      setGraphLockedState(false);
    });
    const vidSidbyte = () => verktygslasKlient.tomVidSidbyte();
    window.addEventListener("pagehide", vidSidbyte);
    return () => {
      window.removeEventListener("pagehide", vidSidbyte);
      verktygslasKlient.sattLokalSlapp(null);
    };
  }, []);
  const setMultiplicationTableEnabled = useCallback(
    (b: boolean) => setMultiplicationTableEnabledState(b),
    [],
  );

  return (
    <Ctx.Provider
      value={{
        calcLocked,
        graphLocked,
        multiplicationTableEnabled,
        setCalcLocked,
        setGraphLocked,
        setMultiplicationTableEnabled,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useToolLock(): ToolLockContextValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useToolLock must be used inside ToolLockProvider");
  }
  return v;
}
